import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SLIDES = [
  {
    src: '/landing/ui-analytics.png',
    alt: 'PricingTable AI analytics dashboard showing overview metrics and session statistics',
    title: 'Analytics built for pricing pages',
    caption: 'See sessions, engagement, and event types without digging through raw logs.',
  },
  {
    src: '/landing/ui-optimization.png',
    alt: 'Pricing optimization dashboard with start optimization flow and recent jobs',
    title: 'AI optimization you can run and track',
    caption: 'Kick off runs, monitor progress, and open results from one straightforward workspace.',
  },
  {
    src: '/landing/ui-upload.png',
    alt: 'Upload pricing table screen with HTML and CSS paste areas',
    title: 'Bring your own table, fast',
    caption: 'Paste or upload HTML and CSS so you stay in control of structure and styling.',
  },
] as const;

export default function FeaturesUICarousel() {
  const [index, setIndex] = useState(0);
  const n = SLIDES.length;

  const go = useCallback(
    (dir: -1 | 1) => {
      setIndex((i) => (i + dir + n) % n);
    },
    [n],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go]);

  const slide = SLIDES[index];

  return (
    <div
      className='mx-auto mt-10 max-w-[36.8rem] px-2 sm:max-w-[41.4rem] sm:px-0'
      role='region'
      aria-roledescription='carousel'
      aria-label='Product interface screenshots'
    >
      <p className='mb-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'>
        Intuitive layout, analytical depth
      </p>
      <div className='flex items-center gap-2 sm:gap-3'>
        <button
          type='button'
          onClick={() => go(-1)}
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-stroke dark:bg-boxdark dark:text-white dark:hover:bg-strokedark'
          aria-label='Previous screenshot'
        >
          <ChevronLeft className='h-4 w-4' aria-hidden />
        </button>
        <div className='min-w-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-md ring-1 ring-black/5 dark:border-stroke dark:bg-boxdark-2 dark:ring-white/10'>
          <div className='aspect-[16/10] w-full'>
            <img
              key={slide.src}
              src={slide.src}
              alt={slide.alt}
              className='h-full w-full object-contain object-left object-top'
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </div>
        </div>
        <button
          type='button'
          onClick={() => go(1)}
          className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-800 shadow-sm transition hover:bg-gray-50 dark:border-stroke dark:bg-boxdark dark:text-white dark:hover:bg-strokedark'
          aria-label='Next screenshot'
        >
          <ChevronRight className='h-4 w-4' aria-hidden />
        </button>
      </div>
      <div className='mt-3 rounded-lg border border-gray-200 bg-white/95 px-3 py-2.5 dark:border-stroke dark:bg-boxdark/95'>
        <p className='text-sm font-semibold text-gray-900 dark:text-white'>{slide.title}</p>
        <p className='mt-0.5 text-xs leading-relaxed text-gray-600 dark:text-gray-300'>{slide.caption}</p>
      </div>
      <div className='mt-3 flex justify-center gap-1.5' role='tablist' aria-label='Screenshot selection'>
        {SLIDES.map((s, i) => (
          <button
            key={s.src}
            type='button'
            role='tab'
            aria-selected={i === index}
            aria-label={`Show slide ${i + 1}: ${s.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-6 bg-yellow-500' : 'w-1.5 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
